import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

// Zakóduje nahraté PCM chunky do WAV (mono, 16-bit) s převzorkováním na targetRate (8 kHz pro telefon).
function encodeWav(chunks, srcRate, targetRate) {
  let total = chunks.reduce((a, c) => a + c.length, 0);
  const flat = new Float32Array(total);
  let off = 0;
  chunks.forEach(c => { flat.set(c, off); off += c.length; });
  const ratio = srcRate / targetRate;
  const outLen = Math.max(1, Math.floor(flat.length / ratio));
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) out[i] = flat[Math.floor(i * ratio)] || 0;
  const buffer = new ArrayBuffer(44 + out.length * 2);
  const view = new DataView(buffer);
  const ws = (o, s) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
  ws(0, 'RIFF'); view.setUint32(4, 36 + out.length * 2, true); ws(8, 'WAVE');
  ws(12, 'fmt '); view.setUint32(16, 16, true); view.setUint16(20, 1, true); view.setUint16(22, 1, true);
  view.setUint32(24, targetRate, true); view.setUint32(28, targetRate * 2, true); view.setUint16(32, 2, true); view.setUint16(34, 16, true);
  ws(36, 'data'); view.setUint32(40, out.length * 2, true);
  let o = 44;
  for (let i = 0; i < out.length; i++) { const s = Math.max(-1, Math.min(1, out[i])); view.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7FFF, true); o += 2; }
  return new Blob([view], { type: 'audio/wav' });
}

// Pole pro IVR audio: přehrávač + nahrání souboru (MP3/WAV) + nahrání přímo z mikrofonu.
function IvrAudioField({ audioId, onChange, isDarkMode, upload }) {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const ctxRef = useRef(null); const procRef = useRef(null); const srcRef = useRef(null);
  const streamRef = useRef(null); const dataRef = useRef([]); const rateRef = useRef(44100); const timerRef = useRef(null);

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      ctxRef.current = ctx; rateRef.current = ctx.sampleRate;
      const src = ctx.createMediaStreamSource(stream); srcRef.current = src;
      const proc = ctx.createScriptProcessor(4096, 1, 1); procRef.current = proc;
      dataRef.current = [];
      proc.onaudioprocess = (e) => { dataRef.current.push(new Float32Array(e.inputBuffer.getChannelData(0))); };
      src.connect(proc); proc.connect(ctx.destination);
      setSeconds(0); setRecording(true);
      timerRef.current = setInterval(() => setSeconds(s => s + 1), 1000);
    } catch (e) { alert('Nepodařilo se spustit mikrofon: ' + (e?.message || e)); }
  };

  const stopRec = async () => {
    clearInterval(timerRef.current);
    setRecording(false);
    try { procRef.current && procRef.current.disconnect(); } catch { /* */ }
    try { srcRef.current && srcRef.current.disconnect(); } catch { /* */ }
    try { streamRef.current && streamRef.current.getTracks().forEach(t => t.stop()); } catch { /* */ }
    const blob = encodeWav(dataRef.current, rateRef.current, 8000);
    try { ctxRef.current && ctxRef.current.close(); } catch { /* */ }
    setBusy(true);
    const file = new File([blob], 'nahravka.wav', { type: 'audio/wav' });
    const id = await upload(file);
    setBusy(false);
    if (id) onChange(id);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setBusy(true);
    const id = await upload(f);
    setBusy(false);
    if (id) onChange(id);
  };

  return (
    <div className="flex items-center gap-2 mt-2 flex-wrap">
      {audioId && (
        <>
          <audio controls preload="none" src={`${API_URL}/twilio/ivr-audio/${audioId}?t=${audioId}`} style={{ colorScheme: isDarkMode ? 'dark' : 'light' }} className="h-8" />
          <button onClick={() => onChange(null)} className="text-xs px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white">Odebrat</button>
        </>
      )}
      {recording ? (
        <button onClick={stopRec} className="text-xs px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white animate-pulse">⏹ Zastavit nahrávání ({seconds}s)</button>
      ) : (
        <button onClick={startRec} disabled={busy} className="text-xs px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-700 text-white disabled:bg-gray-400">🎤 Nahrát z mikrofonu</button>
      )}
      {!recording && (
        <label className={`text-xs px-3 py-1.5 rounded cursor-pointer ${busy ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'} text-white`}>
          🎵 Nahrát MP3/WAV
          <input type="file" accept=".mp3,.wav,audio/mpeg,audio/wav,audio/x-wav" className="hidden" onChange={onFile} disabled={busy} />
        </label>
      )}
      {busy && <span className="text-xs text-gray-400">Nahrávám…</span>}
      {!audioId && !recording && !busy && <span className="text-xs text-gray-400">Nahraj z mikrofonu, nebo vlož MP3/WAV</span>}
    </div>
  );
}

/**
 * TwilioSettings — admin stránka pro nastavení volání přes Twilio.
 * Route: /twilio-settings (jen admin)
 *  - povolení funkce, čísla + přiřazení obchodníkům, povolení softphonu uživatelům
 *  - nahrávání hovorů on/off + retenční doba
 *  - přehled hovorů a přehrání nahrávek (uložených na Cloudu)
 */
export default function TwilioSettings({ isDarkMode, userRole, currentUserId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({ enabled: false, numbers: [], recordingEnabled: false, recordingRetentionDays: 30 });
  const [credentials, setCredentials] = useState(null);
  const [sipPasswords, setSipPasswords] = useState({}); // { userId: nové SIP heslo } (prázdné = nezměněno)
  const [users, setUsers] = useState([]);
  const [enabledUsers, setEnabledUsers] = useState([]); // userId[]
  const [userNumbers, setUserNumbers] = useState({}); // { userId: 'čísla oddělená čárkou' }
  const [calls, setCalls] = useState([]);
  const [callSearch, setCallSearch] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [tab, setTab] = useState('connection'); // connection | recording | users | calls
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const card = `rounded-lg border p-4 ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} shadow-sm`;
  const label = `block text-sm font-semibold mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`;
  const input = `w-full px-3 py-2 rounded border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/twilio/config?userId=${currentUserId}`);
      if (res.ok) {
        const data = await res.json();
        // Přidej stabilní _key k SIP linkám (pro editaci více linek na uživatele)
        if (data.config) {
          data.config.sipLines = (data.config.sipLines || []).map((l, i) => ({
            ...l, _key: `k${i}_${Math.random().toString(36).slice(2, 8)}`
          }));
        }
        setConfig(data.config);
        setCredentials(data.credentials);
        setUsers(data.users || []);
        setEnabledUsers((data.users || []).filter(u => u.twilioEnabled).map(u => String(u._id)));
        // Sestav per-user čísla z config.numbers (podle přiřazení)
        const map = {};
        (data.config.numbers || []).forEach(n => {
          if (n.assignedUserId) {
            const k = String(n.assignedUserId);
            map[k] = map[k] ? `${map[k]}, ${n.phoneNumber}` : n.phoneNumber;
          }
        });
        setUserNumbers(map);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [currentUserId]);

  const fetchCalls = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/twilio/calls?userId=${currentUserId}&limit=200`);
      if (res.ok) { const d = await res.json(); setCalls(d.calls || []); }
    } catch (e) { console.error(e); }
  }, [currentUserId]);

  const filteredCalls = calls.filter(c => {
    const q = callSearch.trim().toLocaleLowerCase('cs');
    if (!q) return true;
    return [c.companyName, c.agentName, c.fromNumber, c.toNumber].some(v => (v || '').toLocaleLowerCase('cs').includes(q));
  });

  const downloadRecording = async (c) => {
    setDownloadingId(c._id);
    try {
      const res = await fetch(`${API_URL}/twilio/recording-file/${c._id}?userId=${currentUserId}`);
      if (!res.ok) { alert('Nahrávku se nepodařilo stáhnout'); return; }
      const blob = await res.blob();
      const ext = (blob.type || '').includes('webm') ? 'webm' : 'mp3';
      const when = c.startedAt ? new Date(c.startedAt).toISOString().slice(0, 16).replace(/[:T]/g, '-') : c._id;
      const safeName = (c.companyName || c.agentName || 'hovor').replace(/[^\p{L}\p{N}]+/gu, '_');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}_${when}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Nahrávku se nepodařilo stáhnout'); }
    finally { setDownloadingId(null); }
  };

  const syncRecordings = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/twilio/sync-recordings?userId=${currentUserId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: currentUserId })
      });
      const d = await res.json();
      if (res.ok) {
        alert(
          `Nahrávání v CRM: ${d.recordingEnabledInCrm ? 'ZAPNUTO ✅' : 'VYPNUTO ❌ (zaškrtni „Nahrávat hovory" a ulož!)'}\n\n` +
          `Twilio má celkem ${d.twilioRecordingsTotal} nahrávek.\n` +
          `Staženo do Cloudu: ${d.downloaded}\nSpárováno s hovory: ${d.linked}\nUž bylo uloženo: ${d.skipped}` +
          (d.twilioRecordingsTotal === 0
            ? '\n\n⚠️ Twilio nemá žádné nahrávky – hovory se zatím nenahrály. Zapni nahrávání v CRM, ulož, a udělej nový zvednutý hovor.'
            : ''));
        await fetchCalls();
      } else {
        alert(d.error || 'Chyba při synchronizaci');
      }
    } catch { alert('Chyba připojení'); }
    finally { setSyncing(false); }
  };

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { if (tab === 'calls') fetchCalls(); }, [tab, fetchCalls]);

  const save = async () => {
    setSaving(true);
    try {
      // Sestav čísla z per-user polí (víc čísel oddělených čárkou)
      const numbers = [];
      Object.entries(userNumbers).forEach(([uid, str]) => {
        (str || '').split(',').map(s => s.trim()).filter(Boolean).forEach(pn => {
          numbers.push({ phoneNumber: pn, label: '', assignedUserId: uid });
        });
      });
      const res = await fetch(`${API_URL}/twilio/config?userId=${currentUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: currentUserId,
          enabled: config.enabled,
          recordingEnabled: config.recordingEnabled,
          recordingRetentionDays: config.recordingRetentionDays,
          numbers,
          enabledUsers,
          ownerRoutingEnabled: config.ownerRoutingEnabled,
          ringTimeoutSec: config.ringTimeoutSec,
          ivrEnabled: config.ivrEnabled,
          ivrConnectingAudio: config.ivrConnectingAudio,
          ivrWaitMusic: config.ivrWaitMusic,
          ivrGreeting: config.ivrGreeting,
          ivrGreetingAudio: config.ivrGreetingAudio,
          ivrNoAnswer: config.ivrNoAnswer,
          ivrNoAnswerAudio: config.ivrNoAnswerAudio,
          ivrNoAnswerForward: config.ivrNoAnswerForward,
          ivrOptions: config.ivrOptions,
          provider: config.provider || 'twilio',
          odorikMode: 'webrtc',
          sipLines: (config.sipLines || []).map(l => ({
            assignedUserId: l.assignedUserId,
            line: (l.line || '').trim(),
            callerId: (l.callerId || '').trim(),
            label: (l.label || '').trim(),
            password: sipPasswords[l._key] || ''
          }))
        })
      });
      if (res.ok) {
        await fetchConfig();
        setSipPasswords({});
        alert('Nastavení VoIP uloženo.');
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.error || 'Chyba při ukládání');
      }
    } catch { alert('Chyba připojení'); }
    finally { setSaving(false); }
  };

  const toggleUser = (uid) => setEnabledUsers(list => list.includes(uid) ? list.filter(x => x !== uid) : [...list, uid]);

  // SIP linky (Odorik WebRTC) – více linek na uživatele, editace přes stabilní _key
  const sipLinesForUser = (uid) => (config.sipLines || []).filter(l => String(l.assignedUserId) === String(uid));
  const updateSipLine = (key, field, value) => setConfig(c => ({
    ...c,
    sipLines: (c.sipLines || []).map(l => l._key === key ? { ...l, [field]: value } : l)
  }));
  const addSipLine = (uid) => setConfig(c => ({
    ...c,
    sipLines: [...(c.sipLines || []), { assignedUserId: uid, line: '', callerId: '', label: '', _key: `k${Date.now()}_${Math.random().toString(36).slice(2, 6)}` }]
  }));
  const removeSipLine = (key) => {
    setConfig(c => ({ ...c, sipLines: (c.sipLines || []).filter(l => l._key !== key) }));
    setSipPasswords(m => { const n = { ...m }; delete n[key]; return n; });
  };

  // IVR editace
  const addOption = () => setConfig(c => ({ ...c, ivrOptions: [...(c.ivrOptions || []), { digit: '', say: '', targetUserIds: [] }] }));
  const updateOption = (idx, field, value) => setConfig(c => {
    const opts = [...(c.ivrOptions || [])];
    opts[idx] = { ...opts[idx], [field]: value };
    return { ...c, ivrOptions: opts };
  });
  const removeOption = (idx) => setConfig(c => ({ ...c, ivrOptions: (c.ivrOptions || []).filter((_, i) => i !== idx) }));
  const toggleOptionUser = (idx, uid) => setConfig(c => {
    const opts = [...(c.ivrOptions || [])];
    const cur = (opts[idx].targetUserIds || []).map(String);
    opts[idx] = { ...opts[idx], targetUserIds: cur.includes(uid) ? cur.filter(x => x !== uid) : [...cur, uid] };
    return { ...c, ivrOptions: opts };
  });

  const uploadIvrAudio = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('userId', currentUserId);
    try {
      const res = await fetch(`${API_URL}/twilio/ivr-audio?userId=${currentUserId}`, { method: 'POST', body: fd });
      if (res.ok) return (await res.json()).id;
      const e = await res.json().catch(() => ({}));
      alert(e.error || 'Chyba při nahrávání audia');
    } catch { alert('Chyba připojení'); }
    return null;
  };

  const runDiagnose = async () => {
    setDiagLoading(true);
    setDiag(null);
    try {
      const res = await fetch(`${API_URL}/twilio/diagnose?userId=${currentUserId}`);
      const data = await res.json();
      if (res.ok) setDiag(data);
      else setDiag({ error: data.error || 'Chyba' });
    } catch { setDiag({ error: 'Chyba připojení' }); }
    finally { setDiagLoading(false); }
  };

  if (userRole !== 'admin' && userRole !== 'superadmin') {
    return (
      <div className={`min-h-screen p-8 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'}`}>
        <h1 className="text-2xl font-bold text-red-600">🚫 Přístup odepřen</h1>
      </div>
    );
  }

  const CredRow = ({ ok, name, hint }) => (
    <div className="flex items-center gap-2 text-sm">
      <span>{ok ? '✅' : '❌'}</span>
      <span className={`font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{name}</span>
      {!ok && <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{hint}</span>}
    </div>
  );

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className={`${tab === 'calls' ? 'max-w-[1800px]' : 'max-w-7xl'} mx-auto p-6 pb-24 space-y-6`}>
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold">☎️ VoIP – volání z CRM</h1>
          <button onClick={() => navigate('/users')} className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}>← Zpět</button>
        </div>

        <div className="flex gap-2 flex-wrap">
          {[
            { id: 'connection', label: '🔌 Připojení' },
            { id: 'odorik', label: '📞 Odorik linky' },
            { id: 'recording', label: '🎙️ Nahrávání' },
            { id: 'inbound', label: '📥 Příchozí / IVR' },
            { id: 'calls', label: '📞 Hovory a nahrávky' },
            { id: 'users', label: '👥 Uživatelé a čísla (Twilio)' },
          ].filter(t => t.id !== 'odorik' || (config.provider || 'twilio') === 'odorik').map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-4 py-2 rounded text-sm font-semibold ${tab === t.id ? 'bg-blue-600 text-white' : (isDarkMode ? 'bg-gray-700' : 'bg-gray-200')}`}>{t.label}</button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500">Načítám…</div>
        ) : tab === 'connection' ? (
          <>
            {/* Výběr poskytovatele VoIP */}
            <div className={card}>
              <h2 className="font-semibold mb-3">📡 Poskytovatel volání</h2>
              <div className="flex gap-3 flex-wrap">
                {[
                  { id: 'twilio', label: '☎️ Twilio', desc: 'Softphone v prohlížeči (odchozí i příchozí, nahrávání, IVR)' },
                  { id: 'odorik', label: '📞 Odorik', desc: 'Přímé volání z prohlížeče (SIP/WebRTC), vlastní české číslo' },
                ].map(p => {
                  const active = (config.provider || 'twilio') === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setConfig(c => ({ ...c, provider: p.id }))}
                      className={`flex-1 min-w-[220px] text-left rounded-lg border p-3 transition ${active ? 'border-blue-500 ring-2 ring-blue-500/40' : (isDarkMode ? 'border-gray-700 hover:border-gray-500' : 'border-gray-300 hover:border-gray-400')}`}
                    >
                      <div className="font-semibold">{active ? '🔘 ' : '⚪ '}{p.label}</div>
                      <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{p.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {(config.provider || 'twilio') === 'twilio' ? (
              /* Stav přihlašovacích údajů Twilio (ENV na serveru) */
              <div className={card}>
                <h2 className="font-semibold mb-3">🔐 Přihlašovací údaje (server / ENV)</h2>
                {credentials && (
                  <div className="space-y-1">
                    <CredRow ok={credentials.packageInstalled} name="twilio (npm balíček)" hint="spusť `npm install twilio` v crm-backend" />
                    <CredRow ok={credentials.accountSid} name="TWILIO_ACCOUNT_SID" hint="nastav ENV proměnnou na serveru" />
                    <CredRow ok={credentials.authToken} name="TWILIO_AUTH_TOKEN" hint="nastav ENV proměnnou na serveru" />
                    <CredRow ok={credentials.apiKey} name="TWILIO_API_KEY_SID + SECRET" hint="nastav ENV proměnné na serveru" />
                    <CredRow ok={credentials.twimlApp} name="TWILIO_TWIML_APP_SID" hint="nastav ENV proměnnou na serveru" />
                  </div>
                )}
                <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Citlivé údaje se z bezpečnostních důvodů nastavují jako ENV proměnné na serveru (Render), ne v tomto formuláři.
                </p>
                <div className="mt-3 flex items-center gap-3">
                  <button onClick={runDiagnose} disabled={diagLoading} className="px-3 py-1.5 rounded text-sm bg-indigo-600 hover:bg-indigo-700 text-white disabled:bg-gray-400">
                    {diagLoading ? 'Testuji…' : '🔍 Otestovat připojení'}
                  </button>
                </div>
                {diag && (
                <div className={`mt-3 rounded-lg p-3 text-sm ${isDarkMode ? 'bg-gray-900 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`}>
                  {diag.error ? (
                    <div className="text-red-500">❌ {diag.error}</div>
                  ) : (
                    <div className="space-y-1">
                      <div>{diag.authTokenValid ? '✅' : '❌'} Account SID + Auth Token {diag.accountName ? `(účet: ${diag.accountName}, stav: ${diag.accountStatus})` : ''}</div>
                      <div>{diag.apiKeyValid ? '✅' : '❌'} API Key SID + Secret {diag.apiKeyValid === false ? '— klíč je špatně nebo nepatří k tomuto účtu' : ''}</div>
                      <div>{diag.tokenGenerated ? '✅' : '❌'} Vygenerování Voice tokenu</div>
                      {diag.formats && (
                        <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} text-xs font-mono space-y-0.5`}>
                          <div className="font-sans font-semibold mb-1">Co server dostal z ENV (délka / formát):</div>
                          <div>{diag.formats.accountSid.ok ? '✅' : '⚠️'} ACCOUNT_SID: {diag.formats.accountSid.len} znaků {diag.formats.accountSid.preview} {diag.formats.accountSid.ok ? '' : '(má být 34: AC+32 hex)'}</div>
                          <div>{diag.formats.authToken.ok ? '✅' : '⚠️'} AUTH_TOKEN: {diag.formats.authToken.len} znaků {diag.formats.authToken.ok ? '' : '(má být 32)'}</div>
                          <div>{diag.formats.apiKeySid.ok ? '✅' : '⚠️'} API_KEY_SID: {diag.formats.apiKeySid.len} znaků {diag.formats.apiKeySid.preview} {diag.formats.apiKeySid.ok ? '' : '(má být 34: SK+32 hex)'}</div>
                          <div>{diag.formats.apiKeySecret.ok ? '✅' : '⚠️'} API_KEY_SECRET: {diag.formats.apiKeySecret.len} znaků {diag.formats.apiKeySecret.preview || ''} {diag.formats.apiKeySecret.ok ? '' : '(má být 32)'}</div>
                          <div>{diag.formats.twimlAppSid.ok ? '✅' : '⚠️'} TWIML_APP_SID: {diag.formats.twimlAppSid.len} znaků {diag.formats.twimlAppSid.preview} {diag.formats.twimlAppSid.ok ? '' : '(má být 34: AP+32 hex)'}</div>
                        </div>
                      )}
                      {(diag.details || []).map((d, i) => <div key={i} className="text-xs text-red-500">• {d}</div>)}
                      {diag.authTokenValid && !diag.apiKeyValid && (
                        <div className="text-xs mt-2 text-amber-500">
                          → Vytvoř nový API Key v Twilio Console (Account → API keys) POD tímto účtem a nahraď TWILIO_API_KEY_SID + TWILIO_API_KEY_SECRET na Renderu.
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}
              </div>
            ) : (
              /* Odorik – přímé WebRTC volání z prohlížeče */
              <div className={card}>
                <h2 className="font-semibold mb-2">📞 Odorik – přímé volání z prohlížeče (SIP/WebRTC)</h2>
                <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Volá se přímo z prohlížeče přes tvoje Odorik SIP linky. Přiřazení linek uživatelům (i více linek na jednoho uživatele) a povolení volání spravuj v záložce <strong>📞 Odorik linky</strong>.
                </p>
                <button onClick={() => setTab('odorik')} className="mt-3 px-4 py-2 rounded text-sm bg-blue-600 hover:bg-blue-700 text-white font-semibold">
                  Otevřít Odorik linky →
                </button>
              </div>
            )}

            {/* Hlavní vypínač */}
            <div className={card}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={config.enabled} onChange={e => setConfig(c => ({ ...c, enabled: e.target.checked }))} className="w-5 h-5" />
                <span className="font-semibold">Povolit volání přes VoIP (hlavní vypínač)</span>
              </label>
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {saving ? 'Ukládám…' : '💾 Uložit nastavení'}
              </button>
            </div>
          </>
        ) : tab === 'odorik' ? (
          <>
            <div className={card}>
              <h2 className="font-semibold mb-1">📞 Odorik linky – přiřazení a povolení volání</h2>
              <div className={`rounded-lg p-3 text-xs mb-4 ${isDarkMode ? 'bg-gray-900 border border-gray-700 text-gray-300' : 'bg-blue-50 border border-blue-200 text-gray-700'}`}>
                Každému uživateli můžeš přiřadit <strong>jednu i více SIP linek</strong> (mezi kterými si pak přepíná na spodní liště). Každá linka = interní linka v Odoriku (zdarma) s <strong>aktivním SIP</strong>. Zadej <strong>6místné číslo linky</strong>, její <strong>SIP heslo</strong>, případně <strong>zobrazené číslo (CLI)</strong> a <strong>popisek</strong>. Zaškrtnutím povolíš uživateli volací panel a volání přes VoIP.
              </div>
              <div className="space-y-4">
                {users.map(u => {
                  const uid = String(u._id);
                  const on = enabledUsers.includes(uid);
                  const userLines = sipLinesForUser(uid);
                  return (
                    <div key={uid} className={`rounded-lg border p-3 ${on ? (isDarkMode ? 'border-blue-700 bg-blue-900/10' : 'border-blue-300 bg-blue-50/40') : (isDarkMode ? 'border-gray-700' : 'border-gray-200')}`}>
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={on} onChange={() => toggleUser(uid)} className="w-5 h-5" />
                          <span className="font-medium">{u.name}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{u.role}</span>
                        </label>
                        <button onClick={() => addSipLine(uid)} className="text-xs px-2 py-1 rounded bg-green-600 hover:bg-green-700 text-white">➕ Přidat linku</button>
                      </div>
                      {userLines.length === 0 ? (
                        <div className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Žádná linka – přidej tlačítkem „Přidat linku".</div>
                      ) : (
                        <div className="space-y-2">
                          {userLines.map(l => (
                            <div key={l._key} className="flex items-center gap-2 flex-wrap">
                              <input value={l.line || ''} onChange={e => updateSipLine(l._key, 'line', e.target.value)} placeholder="Číslo linky (např. 300116)" className={`${input} font-mono w-40`} />
                              <input type="password" value={sipPasswords[l._key] || ''} onChange={e => setSipPasswords(m => ({ ...m, [l._key]: e.target.value }))}
                                placeholder={l.hasPassword ? '•••••• (uloženo)' : 'SIP heslo linky'} className={`${input} w-40`} autoComplete="new-password" />
                              <input value={l.callerId || ''} onChange={e => updateSipLine(l._key, 'callerId', e.target.value)} placeholder="Zobrazené číslo (CLI) +420…" className={`${input} font-mono w-44`} />
                              <input value={l.label || ''} onChange={e => updateSipLine(l._key, 'label', e.target.value)} placeholder="Popisek (např. Prodej)" className={`${input} w-36`} />
                              <button onClick={() => removeSipLine(l._key)} title="Odebrat linku" className="text-xs px-2 py-2 rounded bg-red-600 hover:bg-red-700 text-white">🗑️</button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className={`text-xs mt-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                SIP heslo najdeš v Odoriku po kliknutí na číslo linky. Přesměrování při nezvednutí a IVR (hlasový strom / rovnou na obchodníka) se nastavuje přímo u linky v administraci Odoriku.
              </p>
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {saving ? 'Ukládám…' : '💾 Uložit nastavení'}
              </button>
            </div>
          </>
        ) : tab === 'recording' ? (
          <>
            {/* Nahrávání hovorů */}
            <div className={card}>
              <h2 className="font-semibold mb-3">🎙️ Nahrávání hovorů</h2>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={config.recordingEnabled} onChange={e => setConfig(c => ({ ...c, recordingEnabled: e.target.checked }))} className="w-5 h-5" />
                <span>Nahrávat hovory</span>
              </label>
              {config.recordingEnabled && (
                <div className="max-w-xs">
                  <label className={label}>Uchovávat nahrávky (dní)</label>
                  <input type="number" min={1} max={3650} value={config.recordingRetentionDays}
                    onChange={e => setConfig(c => ({ ...c, recordingRetentionDays: parseInt(e.target.value) || 30 }))}
                    className={input} />
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Nahrávky se ukládají na Cloud a po uplynutí této doby se automaticky smažou. Zobrazit je lze v záložce „Hovory a nahrávky".
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {saving ? 'Ukládám…' : '💾 Uložit nastavení'}
              </button>
            </div>
          </>
        ) : tab === 'users' ? (
          <>
            {/* Uživatelé – volací panel + přidělená čísla (sloučeno) */}
            <div className={card}>
              <h2 className="font-semibold mb-1">👥 Uživatelé – volací panel a přidělená čísla <span className="text-xs font-normal opacity-60">(pro Twilio)</span></h2>
              <div className={`rounded-lg p-2 mb-3 text-xs ${isDarkMode ? 'bg-amber-900/20 border border-amber-800 text-amber-200' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                ⚠️ Tato záložka se týká jen <strong>Twilia</strong>. Pro Odorik nastav SIP linky a povolení volání v záložce <strong>🔌 Připojení</strong> (poskytovatel Odorik).
              </div>
              <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Zaškrtni, kdo má mít volací panel. Do pole zadej Twilio číslo uživatele (víc čísel odděl čárkou) – slouží jako caller ID pro odchozí hovory a pro směrování příchozích.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
                      <th className="px-2 py-2 text-center w-24">Volací panel</th>
                      <th className="px-2 py-2 text-left">Uživatel</th>
                      <th className="px-2 py-2 text-left">Přidělená čísla (caller ID)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(u => {
                      const uid = String(u._id);
                      const on = enabledUsers.includes(uid);
                      return (
                        <tr key={u._id} className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'} ${on ? (isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''}`}>
                          <td className="px-2 py-2 text-center">
                            <input type="checkbox" checked={on} onChange={() => toggleUser(uid)} className="w-5 h-5" />
                          </td>
                          <td className="px-2 py-2">
                            <span className="font-medium">{u.name}</span>
                            <span className={`block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{u.role}</span>
                          </td>
                          <td className="px-2 py-2">
                            <input
                              value={userNumbers[uid] || ''}
                              onChange={e => setUserNumbers(m => ({ ...m, [uid]: e.target.value }))}
                              placeholder="+420123456789 (víc odděl čárkou)"
                              className={`${input} font-mono min-w-[220px]`}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {saving ? 'Ukládám…' : '💾 Uložit nastavení'}
              </button>
            </div>
          </>
        ) : tab === 'inbound' ? (
          <>
            {(config.provider || 'twilio') === 'odorik' && (
              <div className={`rounded-lg p-3 text-sm ${isDarkMode ? 'bg-amber-900/20 border border-amber-800 text-amber-200' : 'bg-amber-50 border border-amber-200 text-amber-800'}`}>
                ⚠️ Toto nastavení IVR je jen pro <strong>Twilio</strong> (hlasový strom hostuje Twilio). U <strong>Odoriku</strong> se IVR nedělá v CRM – zřizuje se jako <strong>samostatná linka přímo v Odoriku</strong> (menu Linky → nová linka → hlasové menu / paralelní zvonění / routování). Když někdo zavolá na to IVR číslo, Odorik ho podle svého stromu spojí s obchodníkem a hovor dorazí do CRM přes SIP normálně. Rád ti to nastavím naživo.
              </div>
            )}
            {/* Přednostní směrování na majitele */}
            <div className={card}>
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" checked={config.ownerRoutingEnabled !== false} onChange={e => setConfig(c => ({ ...c, ownerRoutingEnabled: e.target.checked }))} className="w-5 h-5 mt-0.5" />
                <span>
                  <span className="font-semibold">Přednostně směrovat na majitele zákazníka</span>
                  <span className={`block text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    Podle čísla volajícího se dohledá firma v CRM a hovor se spojí s jejím majitelem (kdo ji má přiřazenou), pokud je online. Když majitel není/nezvedá, spustí se IVR strom níže.
                  </span>
                </span>
              </label>
            </div>

            {/* Znělka/hláška při spojování */}
            <div className={card}>
              <h2 className="font-semibold mb-1">🎵 Znělka/hláška při spojování</h2>
              <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Krátká znělka nebo hláška „Prosím vyčkejte, spojuji vás…", která se přehraje volajícímu těsně před vyzváněním u obchodníka. Doporučeno krátké (pár sekund).
              </p>
              <IvrAudioField audioId={config.ivrConnectingAudio} onChange={id => setConfig(c => ({ ...c, ivrConnectingAudio: id }))} isDarkMode={isDarkMode} upload={uploadIvrAudio} />
            </div>

            {/* Hudba během vyzvánění (konferenční režim) */}
            <div className={card}>
              <h2 className="font-semibold mb-1">🎶 Hudba během vyzvánění (dokud to obchodník nezvedne)</h2>
              <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Když nahraješ hudbu, volající ji slyší po celou dobu, co u tebe v CRM zvoní hovor. Jakmile zvedneš, hudba přestane a mluvíte. (Použije se konferenční režim jen pro volby IVR. Bez hudby zůstane běžné vyzvánění.)
              </p>
              <IvrAudioField audioId={config.ivrWaitMusic} onChange={id => setConfig(c => ({ ...c, ivrWaitMusic: id }))} isDarkMode={isDarkMode} upload={uploadIvrAudio} />
            </div>

            {/* IVR strom */}
            <div className={card}>
              <label className="flex items-center gap-3 cursor-pointer mb-3">
                <input type="checkbox" checked={!!config.ivrEnabled} onChange={e => setConfig(c => ({ ...c, ivrEnabled: e.target.checked }))} className="w-5 h-5" />
                <span className="font-semibold">🌳 Zapnout hlasový strom (IVR)</span>
              </label>

              {config.ivrEnabled && (
                <div className="space-y-4">
                  <div>
                    <label className={label}>Uvítací hláška (přehraje se před nabídkou voleb)</label>
                    <textarea rows={3} value={config.ivrGreeting || ''} onChange={e => setConfig(c => ({ ...c, ivrGreeting: e.target.value }))}
                      placeholder="Např.: Dobrý den, vítejte ve společnosti Vyjednáme, partner společnosti Vodafone. Pro spojení s obchodním zástupcem stiskněte 1. Pro technické oddělení stiskněte 2."
                      className={input} />
                    {<IvrAudioField audioId={config.ivrGreetingAudio} onChange={id => setConfig(c => ({ ...c, ivrGreetingAudio: id }))} isDarkMode={isDarkMode} upload={uploadIvrAudio} />}
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className={label}>Volby (klávesa → skupina)</label>
                      <button onClick={addOption} className="px-3 py-1.5 rounded text-sm bg-green-600 hover:bg-green-700 text-white">➕ Přidat volbu</button>
                    </div>
                    {(config.ivrOptions || []).length === 0 ? (
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Zatím žádné volby. Přidej např. „1 = obchod", „2 = technici".</p>
                    ) : (
                      <div className="space-y-3">
                        {(config.ivrOptions || []).map((opt, idx) => (
                          <div key={idx} className={`rounded-lg border p-3 ${isDarkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-sm font-semibold">Klávesa:</span>
                              <input value={opt.digit || ''} onChange={e => updateOption(idx, 'digit', e.target.value.replace(/\D/g, '').slice(0, 1))} placeholder="1" className={`${input} w-16 text-center font-mono`} />
                              <div className="flex-1" />
                              <button onClick={() => removeOption(idx)} className="px-2 py-1 rounded bg-red-600 hover:bg-red-700 text-white text-sm">🗑️</button>
                            </div>
                            <div className="mb-2">
                              <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Hláška po zvolení (nepovinné)</label>
                              <input value={opt.say || ''} onChange={e => updateOption(idx, 'say', e.target.value)} placeholder="Např.: Přepojuji na obchodního zástupce, prosím vyčkejte." className={input} />
                              {<IvrAudioField audioId={opt.audioFileId} onChange={id => updateOption(idx, 'audioFileId', id)} isDarkMode={isDarkMode} upload={uploadIvrAudio} />}
                            </div>
                            <div>
                              <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Komu zvonit (zaškrtnutí uživatelé, zazvoní online s otevřeným softphonem):</label>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-1 mt-1">
                                {users.map(u => {
                                  const sel = (opt.targetUserIds || []).map(String).includes(String(u._id));
                                  return (
                                    <label key={u._id} className={`flex items-center gap-2 p-1.5 rounded border cursor-pointer text-sm ${sel ? (isDarkMode ? 'border-blue-500 bg-blue-900/20' : 'border-blue-500 bg-blue-50') : (isDarkMode ? 'border-gray-700' : 'border-gray-200')}`}>
                                      <input type="checkbox" checked={sel} onChange={() => toggleOptionUser(idx, String(u._id))} />
                                      <span>{u.name}</span>
                                    </label>
                                  );
                                })}
                              </div>
                            </div>
                            <div className="mt-2">
                              <label className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>📞 Přesměrovat, když tato skupina nezvedne (nepovinné)</label>
                              <input value={opt.forwardNumber || ''} onChange={e => updateOption(idx, 'forwardNumber', e.target.value)} placeholder="+420123456789" className={`${input} font-mono max-w-xs`} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={label}>Hláška, když nikdo nezvedne / neplatná volba</label>
                    <input value={config.ivrNoAnswer || ''} onChange={e => setConfig(c => ({ ...c, ivrNoAnswer: e.target.value }))}
                      placeholder="Omlouváme se, nikdo není momentálně dostupný. Zavolejte prosím později."
                      className={input} />
                    {<IvrAudioField audioId={config.ivrNoAnswerAudio} onChange={id => setConfig(c => ({ ...c, ivrNoAnswerAudio: id }))} isDarkMode={isDarkMode} upload={uploadIvrAudio} />}
                  </div>

                  <div>
                    <label className={label}>📞 Přesměrovat, když nikdo nevezme (globálně)</label>
                    <input value={config.ivrNoAnswerForward || ''} onChange={e => setConfig(c => ({ ...c, ivrNoAnswerForward: e.target.value }))}
                      placeholder="+420123456789 (nechte prázdné = přehraje se jen hláška a zavěsí)"
                      className={`${input} font-mono max-w-xs`} />
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      Když ani majitel, ani zvolená skupina hovor nevezme, přepojí se na toto číslo (např. mobil vedoucího). Jednotlivé volby mohou mít vlastní číslo níže.
                    </p>
                    <div className="mt-3 max-w-xs">
                      <label className={label}>⏱️ Po kolika sekundách přesměrovat</label>
                      <input type="number" min={5} max={120} value={config.ringTimeoutSec || 25}
                        onChange={e => setConfig(c => ({ ...c, ringTimeoutSec: parseInt(e.target.value) || 25 }))}
                        className={input} />
                      <p className={`text-xs mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Jak dlouho se nechá zvonit, než se hovor přesměruje / přehraje závěrečná hláška (5–120 s).
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded bg-blue-600 hover:bg-blue-700 text-white font-semibold disabled:bg-gray-400">
                {saving ? 'Ukládám…' : '💾 Uložit nastavení'}
              </button>
            </div>
          </>
        ) : (
          <div className={card}>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="font-semibold">📞 Historie hovorů a nahrávky</h2>
              <div className="flex gap-2">
                <button onClick={syncRecordings} disabled={syncing} className="px-3 py-1.5 rounded text-sm bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-400">
                  {syncing ? 'Synchronizuji…' : '⬇️ Synchronizovat nahrávky z Twilia'}
                </button>
                <button onClick={fetchCalls} className="px-3 py-1.5 rounded text-sm bg-blue-500 hover:bg-blue-600 text-white">🔄 Načíst</button>
              </div>
            </div>
            <div className="mb-3">
              <input value={callSearch} onChange={e => setCallSearch(e.target.value)} placeholder="🔍 Hledat podle firmy, obchodníka nebo čísla…"
                className={`${input} max-w-sm`} />
              {callSearch && (
                <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{filteredCalls.length} z {calls.length}</span>
              )}
            </div>
            {calls.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Zatím žádné hovory.</p>
            ) : filteredCalls.length === 0 ? (
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Žádné hovory neodpovídají hledání.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`border-b ${isDarkMode ? 'border-gray-700 text-gray-300' : 'border-gray-200 text-gray-600'}`}>
                      <th className="px-2 py-2 text-left">Kdy</th>
                      <th className="px-2 py-2 text-left">Směr</th>
                      <th className="px-2 py-2 text-left">Od</th>
                      <th className="px-2 py-2 text-left">Komu</th>
                      <th className="px-2 py-2 text-left">Firma</th>
                      <th className="px-2 py-2 text-left">Obchodník</th>
                      <th className="px-2 py-2 text-left">Stav</th>
                      <th className="px-2 py-2 text-left">Délka</th>
                      <th className="px-2 py-2 text-left">Nahrávka</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCalls.map(c => (
                      <tr key={c._id} className={`border-b ${isDarkMode ? 'border-gray-800' : 'border-gray-100'}`}>
                        <td className="px-2 py-2 whitespace-nowrap">{c.startedAt ? new Date(c.startedAt).toLocaleString('cs-CZ') : '—'}</td>
                        <td className="px-2 py-2">{c.direction === 'inbound' ? '📥 Příchozí' : '📤 Odchozí'}</td>
                        <td className="px-2 py-2 font-mono">{c.fromNumber || '—'}</td>
                        <td className="px-2 py-2 font-mono">{c.toNumber || '—'}</td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {c.companyId ? (
                            <button onClick={() => navigate(`/companies/${c.companyId}`)} title="Otevřít firmu" className="text-blue-600 dark:text-blue-400 hover:underline text-left">
                              🏢 {c.companyName || 'Otevřít firmu'}
                            </button>
                          ) : (c.companyName || '—')}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">{c.agentName || '—'}</td>
                        <td className="px-2 py-2">{c.status}</td>
                        <td className="px-2 py-2">{c.durationSec ? `${c.durationSec}s` : '—'}</td>
                        <td className="px-2 py-2 min-w-[360px]">
                          {c.recordingCloudFileId ? (
                            <div className="flex items-center gap-2">
                              <audio controls preload="none" src={`${API_URL}/twilio/recording-file/${c._id}?userId=${currentUserId}`} className="h-9 w-full min-w-[300px]" />
                              <button onClick={() => downloadRecording(c)} disabled={downloadingId === c._id} title="Stáhnout nahrávku"
                                className="px-2 py-1.5 rounded text-xs bg-gray-500/20 hover:bg-gray-500/40 disabled:opacity-40 flex-shrink-0">
                                {downloadingId === c._id ? '…' : '⬇️'}
                              </button>
                            </div>
                          ) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
